// @ts-strict-ignore
import React from 'react';
import {
    CaretDownOutlined,
    CaretRightOutlined,
    CloseCircleFilled,
    ExclamationCircleFilled,
    SearchOutlined,
    ThunderboltFilled,
    WarningFilled,
} from '@ant-design/icons';
import { StatusNode } from '../StatusNode';
import { AppPlatform, AppStoreType, ReleaseStepStatus, ReleaseStepType, WorkflowEnabledSteps } from '../../types';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import {
    isHalfFilledStep,
    isInnerFilledStep,
    isPhasedReleasePaused,
    isReleaseStepType,
    isStepHalted,
    statusColorForReleaseStepStatus,
} from '../../helpers';
import { displayTextForReleaseStepType, displayStringForDestination } from '../../helpers/displayHelpers';
import { DestinationIcon } from './shared/components';
import { ROUTER_REDIRECT, ReleaseStepTabType, STEP_RUNS_SUMMARY_TAB_PARAM } from '../../helpers/routeHelpers';
import { antGrey, runwayColors, getNavDropdownMenuStyle } from '../styles/globalStyles';
import { useSidebarColors } from '../styles/themeMode';
import { StepItemLinkContainer, StepItemText, StepItemTextContainer } from './StepItem';

import Tag from 'antd/lib/tag';
import Popover from 'antd/lib/popover';
import Input from 'antd/lib/input';
import Flex from 'antd/lib/flex';
import { RolloutStepRunStatusData, StepRunStatusData } from '@/types/stepRunStatus';
import { Destination } from '@/types/destinations';
import Collapse from 'antd/lib/collapse';
import Tooltip from 'antd/lib/tooltip';
import { RolloutBadge } from './RolloutBadge';
import { Nullable } from '@/typescriptHelpers';

const PopoverStepItem: React.FC<{
    isActive: boolean;
    onClick: () => void;
    children: React.ReactNode;
}> = ({ isActive, onClick, children }) => {
    const sc = useSidebarColors();
    const [isHovered, setIsHovered] = React.useState(false);

    return (
        <div
            onClick={onClick}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 10px',
                cursor: 'pointer',
                borderRadius: 4,
                backgroundColor:
                    isActive ? sc.selectedBg
                    : isHovered ? sc.bg
                    : 'transparent',
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {children}
        </div>
    );
};

interface StepNodeColors {
    showAsNonBlocking: boolean;
    itemColor: string;
    outerBorderColor?: string;
    innerFilled: boolean;
}

const NON_BLOCKING_BORDER_COLOR = antGrey.grey7;

function resolveStepNodeColors(
    status: ReleaseStepStatus,
    stepArchetype: ReleaseStepType,
    isNonBlocking: boolean,
    isPlanRestricted: boolean,
    isFromScraper: boolean,
    baseInnerFilled: boolean,
    isHalted?: boolean,
): StepNodeColors {
    let showAsNonBlocking = isNonBlocking || isPlanRestricted;
    let itemColor = statusColorForReleaseStepStatus(status);
    let outerBorderColor: string | undefined;
    let innerFilled = baseInnerFilled;

    if (isFromScraper) {
        if (isReleaseStepType(stepArchetype)) {
            itemColor = statusColorForReleaseStepStatus(ReleaseStepStatus.Ready);
            innerFilled = false;
        } else {
            showAsNonBlocking = true;
            itemColor = 'transparent';
            outerBorderColor = NON_BLOCKING_BORDER_COLOR;
            innerFilled = false;
        }
    } else if (showAsNonBlocking) {
        itemColor = 'transparent';
        outerBorderColor = NON_BLOCKING_BORDER_COLOR;
        innerFilled = false;
    } else if (isHalted) {
        itemColor = runwayColors.statusRed;
    }

    return { showAsNonBlocking, itemColor, outerBorderColor, innerFilled };
}

interface ReleaseStepsSidebarSingleNodeProps {
    name: string;
    status: ReleaseStepStatus;
    stepArchetype: ReleaseStepType;
    isActive: boolean;
    isExpanded: Nullable<boolean>;
    setIsExpanded: Nullable<(isExpanded: boolean) => void>;
    onClick: () => void;
    isNonBlocking: boolean;
    isPlanRestricted: boolean;
    isFromScraper: boolean;
    shouldShowTail: boolean;
    shouldShowHead: boolean;
    pendingChecklistItemCount: number;
    hasEnabledLifecycleAutomation: boolean;
    hasEnabledPendingLifecycleAutomation: boolean;
    hasEnabledFailedLifecycleAutomation: boolean;
    hasEnabledBlockedLifecycleAutomation: boolean;
    shouldShowTagNeededWarning: boolean;
    isHalfFilledStep: boolean;
    isInnerFilledStep: boolean;
    isHalted?: boolean;
    subStepCount?: number;
    isSubStep?: boolean;
    isLastNode?: boolean;
    isParentSectionActive?: boolean;
    isRolloutPaused?: boolean;
    isRolloutPausedByRunway?: boolean;
    destination?: Destination;
    onChecklistClick?: () => void;
}

const ReleaseStepsSidebarSingleNode: React.FC<ReleaseStepsSidebarSingleNodeProps> = ({
    name,
    status,
    stepArchetype,
    isActive,
    isExpanded,
    onClick,
    isNonBlocking,
    isPlanRestricted,
    isFromScraper,
    shouldShowTail,
    shouldShowHead,
    pendingChecklistItemCount,
    hasEnabledLifecycleAutomation,
    hasEnabledPendingLifecycleAutomation,
    hasEnabledFailedLifecycleAutomation,
    hasEnabledBlockedLifecycleAutomation,
    shouldShowTagNeededWarning,
    isHalfFilledStep,
    isInnerFilledStep,
    isHalted,
    subStepCount,
    isSubStep,
    isLastNode: isLastNodeProp,
    isParentSectionActive,
    isRolloutPaused,
    isRolloutPausedByRunway,
    destination,
    onChecklistClick,
}) => {
    const sc = useSidebarColors();
    const [isHovered, setIsHovered] = React.useState(false);
    const { showAsNonBlocking, itemColor, outerBorderColor, innerFilled } = resolveStepNodeColors(
        status,
        stepArchetype,
        isNonBlocking,
        isPlanRestricted,
        isFromScraper,
        isInnerFilledStep,
        isHalted,
    );

    const node = (
        <StatusNode
            statusColor={itemColor}
            showTail={shouldShowTail}
            showHead={shouldShowHead}
            halfFilled={isHalfFilledStep}
            innerFilled={innerFilled}
            outerBorderColor={outerBorderColor}
            outerBorderWidth={showAsNonBlocking ? 1 : 0}
            shouldShowMiniVersion={isSubStep}
            size={12}
        />
    );

    const releaseStepIsActive = status !== ReleaseStepStatus.Inactive && status !== ReleaseStepStatus.Skipped;

    const automationIconStyle = { marginRight: 8 };
    const automationIcon =
        shouldShowTagNeededWarning ?
            <Tooltip title="This release needs to be tagged.">
                <WarningFilled style={{ color: runwayColors.statusYellow, ...automationIconStyle }} />
            </Tooltip>
        : hasEnabledFailedLifecycleAutomation ?
            <CloseCircleFilled style={{ color: runwayColors.statusRed, ...automationIconStyle }} />
        : hasEnabledBlockedLifecycleAutomation ?
            <ExclamationCircleFilled style={{ color: runwayColors.statusRed, ...automationIconStyle }} />
        : hasEnabledPendingLifecycleAutomation ?
            <ThunderboltFilled style={{ color: runwayColors.info, ...automationIconStyle }} />
        : hasEnabledLifecycleAutomation ?
            <ThunderboltFilled style={{ color: isActive ? sc.text : sc.textMuted, ...automationIconStyle }} />
        :   null;

    const needsAdditionalSpacing = pendingChecklistItemCount > 0 && releaseStepIsActive;

    return (
        <div
            style={{
                cursor: 'pointer',
            }}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <StepItemLinkContainer
                isActive={isActive}
                isExpanded={isExpanded ?? false}
                isSubStep={isSubStep}
                isLastNode={isLastNodeProp}
                isParentSectionActive={isParentSectionActive}
                hasIcon={needsAdditionalSpacing}
                isHovered={isHovered}
            >
                {pendingChecklistItemCount > 0 && releaseStepIsActive && (
                    <Tag
                        style={{
                            color: sc.text,
                            fontSize: 14,
                            borderColor: sc.icon,
                            borderWidth: 1,
                            backgroundColor: 'transparent',
                            cursor: onChecklistClick ? 'pointer' : 'default',
                        }}
                        onClick={
                            onChecklistClick ?
                                (e) => {
                                    e.stopPropagation();
                                    onChecklistClick();
                                }
                            :   undefined
                        }
                    >
                        {pendingChecklistItemCount}
                    </Tag>
                )}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: isSubStep ? 8 : undefined,
                        minWidth: 0,
                    }}
                >
                    {automationIcon}
                    {stepArchetype === ReleaseStepType.Rollout && (
                        <RolloutBadge isRolloutPaused={isRolloutPaused} isPausedByRunway={isRolloutPausedByRunway} />
                    )}
                    {!isSubStep &&
                        subStepCount &&
                        subStepCount > 1 &&
                        (isExpanded ?
                            <CaretDownOutlined style={{ color: sc.textMuted, fontSize: 12, marginRight: 4 }} />
                        :   <CaretRightOutlined style={{ color: sc.textMuted, fontSize: 12, marginRight: 4 }} />)}
                    <Flex align="center" gap={8} style={{ paddingRight: 12, minWidth: 0, overflow: 'hidden' }}>
                        <StepItemTextContainer isSubStep={isSubStep}>
                            <StepItemText
                                shouldBoldText={(isActive || isExpanded) && !isSubStep}
                                nonBlocking={showAsNonBlocking}
                                isSubStep={isSubStep}
                            >
                                {name}
                            </StepItemText>
                        </StepItemTextContainer>
                        {isSubStep && destination && <DestinationIcon destination={destination} size={16} />}
                    </Flex>
                    {node}
                </div>
            </StepItemLinkContainer>
        </div>
    );
};

interface ReleaseStepsSidebarNodeProps {
    appPlatform: AppPlatform | AppPlatform[];
    appStoreType: AppStoreType | AppStoreType[];
    isFirstNode: boolean;
    isLastNode: boolean;
    isTagNeeded: boolean;
    isNonBlocking: boolean;
    isPlanRestricted: boolean;
    isFromScraper: boolean;
    stepRunStatusDatum: StepRunStatusData[];
    stepArchetype: ReleaseStepType;
    allStepRunPaths: string[];
    allStepRunTabPaths: string[];
    isActive: boolean;
    isExpanded: boolean;
    onExpandChange: (expanded: boolean) => void;
    previousNodeHasSubStepsAndIsExpanded: boolean;
    workflowEnabledSteps: WorkflowEnabledSteps;
    isWorkflowsCIStepEnabled: boolean;
    combinedStatus: ReleaseStepStatus;
    customArchetypeGroupName: string;
    destinations?: Destination[];
    stepRunsSummaryRoute?: string;
}

const COLLAPSIBLE_KEY = `1`;
export const ReleaseStepsSidebarNode: React.FC<ReleaseStepsSidebarNodeProps> = ({
    workflowEnabledSteps,
    stepRunStatusDatum,
    stepArchetype,
    appPlatform,
    appStoreType,
    isLastNode,
    isFirstNode,
    isTagNeeded,
    isNonBlocking,
    isPlanRestricted,
    isFromScraper,
    allStepRunPaths,
    allStepRunTabPaths,
    isActive,
    isExpanded,
    onExpandChange,
    previousNodeHasSubStepsAndIsExpanded,
    isWorkflowsCIStepEnabled,
    combinedStatus,
    customArchetypeGroupName,
    destinations = [],
    stepRunsSummaryRoute,
}) => {
    const history = useHistory();
    const location = useLocation();
    const sc = useSidebarColors();
    const firstStepRunStatusData = stepRunStatusDatum[0];
    const hasMoreThanOneStatusData = stepRunStatusDatum.length > 1;

    const [searchValue, setSearchValue] = React.useState('');

    // Auto-expand when the step becomes active (e.g. direct URL load).
    // Only reacts to isActive changes — does not re-run when user manually collapses.
    React.useEffect(() => {
        if (isActive && hasMoreThanOneStatusData) {
            onExpandChange(true);
        }
    }, [isActive, hasMoreThanOneStatusData]);

    React.useEffect(() => {
        if (!isExpanded) {
            setSearchValue('');
        }
    }, [isExpanded]);

    // Prefer the archetype group's own custom name.
    const stepArchetypeName =
        customArchetypeGroupName ? customArchetypeGroupName
        : !hasMoreThanOneStatusData && firstStepRunStatusData?.name ? firstStepRunStatusData.name
        : displayTextForReleaseStepType(stepArchetype, appPlatform, appStoreType, isWorkflowsCIStepEnabled);

    const mergedIsHalfFilledStep = stepRunStatusDatum.some(isHalfFilledStep);
    const mergedIsInnerFilledStep = stepRunStatusDatum.some(isInnerFilledStep);
    const mergedIsHalted = stepRunStatusDatum.some(isStepHalted);
    const mergedChecklistItemCount = stepRunStatusDatum.reduce(
        (acc, stepRunStatusData) => acc + stepRunStatusData.pendingChecklistItemCount,
        0,
    );
    const mergedHasEnabledLifecycleAutomation = stepRunStatusDatum.some(
        (stepRunStatusData) => stepRunStatusData.hasEnabledLifecycleAutomation,
    );
    const mergedHasPendingLifecycleAutomation = stepRunStatusDatum.some(
        (stepRunStatusData) => stepRunStatusData.hasEnabledPendingLifecycleAutomation,
    );
    const mergedHasFailedLifecycleAutomation = stepRunStatusDatum.some(
        (stepRunStatusData) => stepRunStatusData.hasEnabledFailedLifecycleAutomation,
    );
    const mergedHasBlockedLifecycleAutomation = stepRunStatusDatum.some(
        (stepRunStatusData) => stepRunStatusData.hasEnabledBlockedLifecycleAutomation,
    );

    const shouldBeExpanded = hasMoreThanOneStatusData ? isExpanded : isActive; // for steps with substeps, use independent collapse state. Otherwise, expand when active

    const isOnStepRunsSummary =
        !!stepRunsSummaryRoute && !!matchPath(location.pathname, { path: stepRunsSummaryRoute, exact: true });
    const summaryTabParam = new URLSearchParams(location.search).get(STEP_RUNS_SUMMARY_TAB_PARAM);
    const isOnStepRunsSummaryOverviewTab =
        isOnStepRunsSummary && (!summaryTabParam || summaryTabParam === ReleaseStepTabType.Summary);

    const isArchetypeWorkflowsEnabled = workflowEnabledSteps[stepArchetype];
    const baseNodeName =
        isArchetypeWorkflowsEnabled ? firstStepRunStatusData?.name || stepArchetypeName : stepArchetypeName;

    const getPopoverItemMeta = React.useCallback(
        (data: StepRunStatusData, index: number) => {
            const stepName = isArchetypeWorkflowsEnabled ? (data.name ?? stepArchetypeName) : stepArchetypeName;
            const destination = destinations.find((d) => d.stepConfigId === data.stepConfigId) ?? destinations[index];
            const destinationDisplay =
                destination ?
                    displayStringForDestination(
                        destination.platform,
                        destination.appStoreType,
                        destination.name,
                        destination.appName,
                    )
                :   '';
            return { stepName, destination, destinationDisplay };
        },
        [stepArchetypeName, isArchetypeWorkflowsEnabled, destinations],
    );

    const filteredSubSteps = React.useMemo(() => {
        if (stepRunStatusDatum.length <= 1) return [];
        const q = searchValue.trim().toLowerCase();
        return stepRunStatusDatum
            .map((data, index) => ({ data, index }))
            .filter(({ data, index }) => {
                if (!q) return true;
                const { destinationDisplay, stepName } = getPopoverItemMeta(data, index);
                return stepName.toLowerCase().includes(q) || destinationDisplay.toLowerCase().includes(q);
            });
    }, [stepRunStatusDatum, searchValue, getPopoverItemMeta]);

    const checklistRoute =
        mergedChecklistItemCount === 0 ? undefined
        : hasMoreThanOneStatusData ?
            stepRunsSummaryRoute &&
            `${stepRunsSummaryRoute}?${STEP_RUNS_SUMMARY_TAB_PARAM}=${ReleaseStepTabType.Checklist}`
        : allStepRunPaths[0] ? `${allStepRunPaths[0]}/${ReleaseStepTabType.Checklist}`
        : undefined;

    const baseNode = (
        <ReleaseStepsSidebarSingleNode
            stepArchetype={stepArchetype}
            isNonBlocking={isNonBlocking}
            isPlanRestricted={isPlanRestricted}
            isFromScraper={isFromScraper}
            shouldShowTail={!isLastNode && (!hasMoreThanOneStatusData || !shouldBeExpanded)}
            shouldShowHead={!isFirstNode && !previousNodeHasSubStepsAndIsExpanded}
            shouldShowTagNeededWarning={isTagNeeded}
            name={hasMoreThanOneStatusData ? stepArchetypeName : baseNodeName}
            isActive={hasMoreThanOneStatusData ? isOnStepRunsSummary : isActive}
            isExpanded={hasMoreThanOneStatusData ? isExpanded : undefined}
            setIsExpanded={hasMoreThanOneStatusData ? onExpandChange : undefined}
            onClick={() => {
                if (hasMoreThanOneStatusData) {
                    if (isExpanded) {
                        if (isOnStepRunsSummaryOverviewTab) {
                            onExpandChange(false);
                        } else if (stepRunsSummaryRoute) {
                            history.push(stepRunsSummaryRoute, { [ROUTER_REDIRECT]: true });
                        }
                    } else {
                        onExpandChange(true);
                        if (stepRunsSummaryRoute) {
                            history.push(stepRunsSummaryRoute, { [ROUTER_REDIRECT]: true });
                        }
                    }
                } else {
                    history.push(allStepRunTabPaths[0], { [ROUTER_REDIRECT]: true });
                }
            }}
            status={combinedStatus}
            pendingChecklistItemCount={mergedChecklistItemCount}
            onChecklistClick={
                checklistRoute ?
                    () => {
                        if (hasMoreThanOneStatusData) {
                            onExpandChange(true);
                        }
                        history.push(checklistRoute, { [ROUTER_REDIRECT]: true });
                    }
                :   undefined
            }
            hasEnabledLifecycleAutomation={mergedHasEnabledLifecycleAutomation}
            hasEnabledPendingLifecycleAutomation={mergedHasPendingLifecycleAutomation}
            hasEnabledFailedLifecycleAutomation={mergedHasFailedLifecycleAutomation}
            hasEnabledBlockedLifecycleAutomation={mergedHasBlockedLifecycleAutomation}
            isHalfFilledStep={mergedIsHalfFilledStep}
            isInnerFilledStep={mergedIsInnerFilledStep}
            isHalted={mergedIsHalted}
            subStepCount={hasMoreThanOneStatusData ? stepRunStatusDatum.length : undefined}
            isLastNode={isLastNode && (!hasMoreThanOneStatusData || !shouldBeExpanded)}
        />
    );

    if (hasMoreThanOneStatusData) {
        const isSectionActive = isActive;

        const allSubSteps = filteredSubSteps.map(({ data: stepRunStatusData, index }) => {
            const stepName =
                isArchetypeWorkflowsEnabled ? (stepRunStatusData.name ?? stepArchetypeName) : stepArchetypeName;

            const basePath = allStepRunPaths[index];
            const tabPath = allStepRunTabPaths[index];

            // Match against both base path and tab path for this specific substep
            const isSubStepActive = !!matchPath(location.pathname, basePath) || !!matchPath(location.pathname, tabPath);

            const rolloutExtraData = (stepRunStatusData as RolloutStepRunStatusData).extraData;
            const isRolloutPaused =
                stepRunStatusData.extraData && isPhasedReleasePaused(rolloutExtraData.phasedReleaseState);
            const isRolloutPausedByRunway = !!(isRolloutPaused && rolloutExtraData?.isRunwayPaused);

            const destination =
                destinations.find((d) => d.stepConfigId === stepRunStatusData.stepConfigId) ?? destinations[index];

            return (
                <React.Fragment key={`${stepRunStatusData.stepRunId}-${index}`}>
                    <ReleaseStepsSidebarSingleNode
                        name={stepName}
                        status={stepRunStatusData.status}
                        stepArchetype={stepArchetype}
                        isActive={isSubStepActive}
                        isExpanded={undefined}
                        setIsExpanded={undefined}
                        onClick={() => {
                            history.push(basePath, { [ROUTER_REDIRECT]: true });
                        }}
                        isNonBlocking={isNonBlocking}
                        isPlanRestricted={isPlanRestricted}
                        isFromScraper={isFromScraper}
                        shouldShowTail={false}
                        shouldShowHead={false}
                        pendingChecklistItemCount={stepRunStatusData.pendingChecklistItemCount}
                        onChecklistClick={
                            stepRunStatusData.pendingChecklistItemCount > 0 && basePath ?
                                () =>
                                    history.push(`${basePath}/${ReleaseStepTabType.Checklist}`, {
                                        [ROUTER_REDIRECT]: true,
                                    })
                            :   undefined
                        }
                        hasEnabledLifecycleAutomation={stepRunStatusData.hasEnabledLifecycleAutomation}
                        hasEnabledPendingLifecycleAutomation={stepRunStatusData.hasEnabledPendingLifecycleAutomation}
                        hasEnabledFailedLifecycleAutomation={stepRunStatusData.hasEnabledFailedLifecycleAutomation}
                        hasEnabledBlockedLifecycleAutomation={stepRunStatusData.hasEnabledBlockedLifecycleAutomation}
                        shouldShowTagNeededWarning={isTagNeeded}
                        isHalfFilledStep={isHalfFilledStep(stepRunStatusData)}
                        isInnerFilledStep={isInnerFilledStep(stepRunStatusData)}
                        isHalted={isStepHalted(stepRunStatusData)}
                        isSubStep
                        isParentSectionActive={isSectionActive}
                        isRolloutPaused={isRolloutPaused}
                        isRolloutPausedByRunway={isRolloutPausedByRunway}
                        destination={destination}
                    />
                </React.Fragment>
            );
        });
        const popoverContent = (
            <div
                style={{
                    minWidth: 240,
                    maxHeight: 264,
                    overflowY: 'auto',
                }}
            >
                {stepRunStatusDatum.map((stepRunStatusData, index) => {
                    const { stepName, destination } = getPopoverItemMeta(stepRunStatusData, index);
                    const basePath = allStepRunPaths[index];
                    const tabPath = allStepRunTabPaths[index];
                    const isSubStepActive =
                        !!matchPath(location.pathname, basePath) || !!matchPath(location.pathname, tabPath);
                    const pendingCount = stepRunStatusData.pendingChecklistItemCount;
                    const stepIsActive = stepRunStatusData.status !== ReleaseStepStatus.Inactive;

                    const { showAsNonBlocking, itemColor, outerBorderColor, innerFilled } = resolveStepNodeColors(
                        stepRunStatusData.status,
                        stepArchetype,
                        isNonBlocking,
                        isPlanRestricted,
                        isFromScraper,
                        isInnerFilledStep(stepRunStatusData),
                        isStepHalted(stepRunStatusData),
                    );

                    return (
                        <PopoverStepItem
                            key={`${stepRunStatusData.stepRunId}-${index}`}
                            isActive={isSubStepActive}
                            onClick={() => history.push(basePath, { [ROUTER_REDIRECT]: true })}
                        >
                            {pendingCount > 0 && stepIsActive && (
                                <Tag
                                    style={{
                                        color: sc.text,
                                        fontSize: 12,
                                        borderColor: sc.icon,
                                        borderWidth: 1,
                                        backgroundColor: 'transparent',
                                        marginRight: 8,
                                        cursor: 'pointer',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        history.push(`${basePath}/${ReleaseStepTabType.Checklist}`, {
                                            [ROUTER_REDIRECT]: true,
                                        });
                                    }}
                                >
                                    {pendingCount}
                                </Tag>
                            )}
                            <Flex align="center" gap={8} style={{ flex: 1, justifyContent: 'flex-end' }}>
                                <span style={{ color: showAsNonBlocking ? sc.textMuted : sc.text, fontSize: 14 }}>
                                    {stepName}
                                </span>
                                {destination && <DestinationIcon destination={destination} size={20} />}
                                <StatusNode
                                    statusColor={itemColor}
                                    showTail={false}
                                    showHead={false}
                                    halfFilled={isHalfFilledStep(stepRunStatusData)}
                                    innerFilled={innerFilled}
                                    outerBorderColor={outerBorderColor}
                                    outerBorderWidth={showAsNonBlocking ? 1 : 0}
                                    size={10}
                                />
                            </Flex>
                        </PopoverStepItem>
                    );
                })}
            </div>
        );

        return (
            <Collapse
                ghost
                activeKey={shouldBeExpanded ? COLLAPSIBLE_KEY : null}
                expandIcon={() => null}
                collapsible="icon"
                destroyInactivePanel={true}
                className="ant-collapse-sidebar-node"
                items={[
                    {
                        key: COLLAPSIBLE_KEY,
                        label:
                            shouldBeExpanded ? baseNode : (
                                <Popover
                                    content={popoverContent}
                                    overlayInnerStyle={{
                                        ...getNavDropdownMenuStyle(sc),
                                        padding: 0,
                                    }}
                                    arrow={false}
                                    autoAdjustOverflow
                                    placement="bottom"
                                    trigger="hover"
                                    mouseEnterDelay={0.4}
                                >
                                    <div>{baseNode}</div>
                                </Popover>
                            ),
                        children: (
                            <div
                                style={{
                                    borderBottom: isLastNode ? `0.5px solid ${sc.border}` : 'none',
                                    maxHeight: '250px',
                                    overflowY: 'auto',
                                }}
                            >
                                {stepRunStatusDatum.length > 8 && (
                                    <div
                                        style={{
                                            padding: '3px 15px 3px 16px',
                                            backgroundColor: sc.searchBg,
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Input
                                            variant="borderless"
                                            prefix={
                                                searchValue ?
                                                    <SearchOutlined style={{ color: sc.iconMuted, fontSize: 14 }} />
                                                :   undefined
                                            }
                                            suffix={
                                                searchValue ? undefined : (
                                                    <SearchOutlined style={{ color: sc.iconMuted, fontSize: 14 }} />
                                                )
                                            }
                                            allowClear={
                                                searchValue ?
                                                    {
                                                        clearIcon: (
                                                            <CloseCircleFilled
                                                                style={{ color: sc.iconMuted, fontSize: 14 }}
                                                            />
                                                        ),
                                                    }
                                                :   false
                                            }
                                            value={searchValue}
                                            onChange={(e) => setSearchValue(e.target.value)}
                                            style={{ width: '100%' }}
                                            styles={{
                                                affixWrapper: {
                                                    paddingInline: 0,
                                                },
                                                input: {
                                                    color: sc.text,
                                                    textAlign: 'right',
                                                },
                                                suffix: {
                                                    marginInlineStart: 19,
                                                },
                                            }}
                                        />
                                    </div>
                                )}
                                {allSubSteps}
                            </div>
                        ),
                    },
                ]}
            />
        );
    }

    return baseNode;
};
